import { useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import PageV2 from "components/shared/PageV2";
import { useState } from "react";
import { Link } from "react-router-dom";
import { SHOPIFY_STOCK_CATEGORY_ID } from "../inventory/const";
import { FETCH_SHOPIFY_PRODUCTS } from "./graphql";

function parseJSONStringArray(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return null;
  }
}

const ShopifyProductPage = () => {
  const [q, setQ] = useState("");
  const { loading, error, data } = useQuery(FETCH_SHOPIFY_PRODUCTS, {
    variables: {
      instance: "AB",
      stockCategoryId: SHOPIFY_STOCK_CATEGORY_ID,
    },
  });

  const alcStocks = data?.stockCategory?.stocks || [];
  const shopifyProducts = parseJSONStringArray(data?.shopifyProducts);

  console.log("shopifyProducts", shopifyProducts);

  let parseError = null;
  if (!loading && !error && data) {
    if (shopifyProducts === null) {
      parseError = new Error("Failed to parse shopifyProducts data.");
    }
  }

  const alcMap = new Map();
  if (Array.isArray(alcStocks)) {
    alcStocks.forEach((row) => {
      const key = row?.product?.number?.trim().toLowerCase();
      if (!key) return;
      alcMap.set(key, row?.product || null);
    });
  }

  console.log("alcMap", alcMap);

  const keyword = q.trim().toLowerCase();
  const mappedRows = Array.isArray(shopifyProducts)
    ? shopifyProducts.map((row, index) => {
        const sku = row?.sku || "";
        const skuKey = sku.trim().toLowerCase();
        const alcProduct = alcMap.get(skuKey);

        return {
          id: index,
          sku,
          sellableOnlineQuantity: row?.sellableOnlineQuantity ?? 0,
          hasAlcStock: Boolean(alcProduct),
          alcProductLabel: alcProduct
            ? `[${alcProduct.number}] ${alcProduct.name}`
            : "",
        };
      })
    : [];
  const hasMissingAlcProducts = mappedRows.some((row) => !row.hasAlcStock);
  const missingAlcProductsCount = mappedRows.filter(
    (row) => !row.hasAlcStock,
  ).length;

  const rows = mappedRows
    .filter((row) => !keyword || row.sku.toLowerCase().includes(keyword))
    .sort((a, b) => {
      const getPriority = (row) => {
        if (!row.hasAlcStock && Number(row.sellableOnlineQuantity) > 0)
          return 0;
        if (!row.hasAlcStock) return 1;
        return 2;
      };

      const priorityDiff = getPriority(a) - getPriority(b);
      if (priorityDiff !== 0) return priorityDiff;

      return a.sku.localeCompare(b.sku);
    });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;
  if (parseError) return <Errors errors={[parseError]} />;

  return (
    <PageV2
      title="Shopify EU Products"
      backTo="./.."
      toolbar={
        <div>
          <Link
            to={`/sales/inventory/${SHOPIFY_STOCK_CATEGORY_ID}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            ALC B2C Inventory
          </Link>
        </div>
      }
    >
      <div>
        <SearchBar value={q} onChange={setQ} placeholder="Search by SKU" />

        {hasMissingAlcProducts ? (
          <div className="mt-2 text-red-600 font-semibold">
            ⚠️ {missingAlcProductsCount} SKU found in Shopify(currently selling)
            not in ALC. This will cause problems for many of us when handling
            customer orders. Please fix this ASAP. Make sure we are selling only
            products that are in ALC warehouse.
          </div>
        ) : null}
      </div>

      <div className="card p-0 overflow-auto">
        <table className="whitespace-nowrap min-w-full">
          <thead>
            <tr className="sticky top-0 backdrop-blur-xl dark:bg-opacity-90 z-10">
              <th className="text-left px-4 py-3">
                Shopify SKU ({rows.length})
              </th>
              <th className="text-left px-4 py-3">
                Shopify Available Inventory
              </th>
              <th className="text-left px-4 py-3">ALC Product</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.id}
                className={`border-y border-gray-50 dark:border-gray-700 ${
                  row.hasAlcStock
                    ? ""
                    : Number(row.sellableOnlineQuantity) > 0
                      ? "bg-red-50 dark:bg-red-950/20"
                      : "bg-orange-100 dark:bg-yellow-950/20"
                }`}
              >
                <td className="px-4 py-2">
                  #{index + 1}. <b>{row.sku || "-"}</b>
                </td>
                <td className="px-4 py-2">{row.sellableOnlineQuantity}</td>
                <td className="px-4 py-2">
                  {row.hasAlcStock ? (
                    row.alcProductLabel
                  ) : (
                    <div className="text-red-600">🔴 Not Found in ALC</div>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td className="px-4 py-6 opacity-60" colSpan={3}>
                  No products found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </PageV2>
  );
};

export default ShopifyProductPage;
